import { ComponentType, HTMLAttributes } from 'react';

import { IconNames } from '@/shared/state/constants';
import { TGradientColor, TIconNames, TLinearGradients } from '@/shared/types';

export interface IconProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  classes?: string;
  color?: TGradientColor | string;
  gradient?: TLinearGradients;
  name?: TIconNames;
}

type IconComponent = ComponentType<{
  color?: TGradientColor | string;
  gradient?: TLinearGradients;
}>;
type IconModule = { default: IconComponent };

// Vite's import.meta.glob loads all icon modules at build time and tree-shakes
// unused ones. `eager: true` makes lookups synchronous (matches the previous
// require() behavior). Keys are resolved relative to this file.
const iconModules = import.meta.glob<IconModule>('@/shared/components/icons/*.tsx', {
  eager: true
});

const iconRegistry = Object.entries(iconModules).reduce<Record<string, IconComponent>>(
  (registry, [filePath, module]) => {
    // filePath looks like "/src/shared/components/icons/eye-icon.tsx"
    const fileName = filePath
      .split('/')
      .pop()
      ?.replace(/\.tsx$/, '');
    if (fileName) registry[fileName] = module.default;
    return registry;
  },
  {}
);

export const Icon = ({ classes, color, gradient, name = 'eye', ...iconProps }: IconProps) => {
  const IconPath = iconRegistry[IconNames[name]];

  if (!IconPath) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`Icon "${name}" (file: ${IconNames[name]}.tsx) not found.`);
    }
    return null;
  }

  return (
    <span className={classes} {...iconProps}>
      <IconPath color={color} gradient={gradient} />
    </span>
  );
};
